import React from "react";

export interface TableColumn<R> {
  key: string;
  header: React.ReactNode;
  cell: (row: R, index: number) => React.ReactNode;
  align?: "left" | "right" | "center";
  width?: string | number;
  className?: string;
  /** Wenn true: monospace Font + kleinere Größe */
  mono?: boolean;
  /** Wenn true: tabular-nums + right align */
  num?: boolean;
  /** Wenn true: Zelle darf umbrechen (Default: einzeilig, Tabelle scrollt). */
  wrap?: boolean;
  /** Wenn true: auf Telefon-Breite (<640 px) ausgeblendet — Beiwerk, das den
   *  horizontalen Scroll nicht wert ist. */
  hideOnMobile?: boolean;
}

interface TableProps<R> {
  columns: TableColumn<R>[];
  rows: R[];
  rowKey: (row: R, index: number) => string;
  emptyMessage?: React.ReactNode;
  className?: string;
  /** Klick auf eine Zeile (setzt Cursor + Tastaturbedienung per Enter). */
  onRowClick?: (row: R, index: number) => void;
  /** Tooltip je Zeile — nur sinnvoll zusammen mit `onRowClick`. */
  rowTitle?: string;
}

export function Table<R>({
  columns,
  rows,
  rowKey,
  emptyMessage = "Keine Daten",
  className = "",
  onRowClick,
  rowTitle,
}: TableProps<R>) {
  return (
    <div className="ds-table-scroll">
      <table className={"ds-table " + className}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                data-col-optional={c.hideOnMobile ? "true" : undefined}
                className={c.num ? "num" : undefined}
                style={{
                  width: c.width,
                  textAlign: c.num ? "right" : c.align,
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="ds-table-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                className={onRowClick ? "clickable" : undefined}
                title={onRowClick ? rowTitle : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter") onRowClick(row, i);
                      }
                    : undefined
                }
              >
                {columns.map((c) => {
                  const cellClass = [
                    c.num ? "num" : "",
                    c.mono ? "mono" : "",
                    c.wrap ? "wrap" : "",
                    c.className ?? "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <td
                      key={c.key}
                      className={cellClass}
                      data-col-optional={c.hideOnMobile ? "true" : undefined}
                      style={c.align && !c.num ? { textAlign: c.align } : undefined}
                    >
                      {c.cell(row, i)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
